/**
 * The tender a buyer was looking at when they decided to pay — carried through
 * the whole checkout and used to put them back on it afterwards.
 *
 * ## Why this exists
 *
 * Measured on prod 2026-09-18 (30 days, bots and internal excluded): of 98 new
 * buyers, **75 had opened a SPECIFIC tender in the hour before paying** — they
 * were buying that lot's contacts or its documents, not a subscription in the
 * abstract. After the payment only **45** ever came back to it. Thirty people
 * paid for something and then had to go and find it again, through a
 * confirmation screen that offers «В каталог» and «В кабинет» and nothing else.
 *
 * Nothing in the flow carried the id. `/plans` is reached by a bare
 * `<Link to="/plans">`, the sign-in round trip lands on `/` (see
 * `postAuthPath`), and a hosted provider (Payme / Click / OCTO) takes the buyer
 * off-origin entirely and returns them to `/billing/return`. Four hand-offs,
 * none of them carrying the one fact the buyer cares about.
 *
 * ## Shape of the fix
 *
 * Modelled on {@link ./pending-invite.ts} — same stash / read / clear trio,
 * same SSR and private-mode guards — with two deliberate differences:
 *
 *  - **localStorage, not sessionStorage.** The invite belongs to one tab; this
 *    has to survive a full cross-site round trip through a payment provider,
 *    which on a phone routinely returns in a DIFFERENT tab (the Payme app opens
 *    its own). sessionStorage would be empty exactly for the mobile buyers this
 *    is for.
 *  - **A TTL**, because localStorage has none of its own. Two hours: long
 *    enough for a bank transfer redirect plus an SMS code, short enough that a
 *    tender read on Monday never hijacks Friday's unrelated purchase.
 *
 * Reads that CONSUME the context ({@link takePendingCheckout}) clear it in the
 * same breath — one-shot, so a second payment cannot inherit the first one's
 * tender.
 *
 * ## Why the values are validated on the way out
 *
 * `tenderId` and `planKey` both enter from the address bar (`/plans?tender=…`,
 * `/plans?plan=…`) and leave as a PATH we navigate to (`/tender/<id>`). An
 * unvalidated value is an open-redirect primitive: `?tender=//evil.example`
 * would assemble `/tender///evil.example`. Both are opaque tokens in practice —
 * a cuid and a plan key — so they are held to `[A-Za-z0-9_-]`, which cannot
 * express a scheme, a host or a path separator. Anything else is treated as
 * absent rather than sanitised into something almost-right.
 */

export const PENDING_CHECKOUT_KEY = 'pendingCheckout';

/** Two hours. See the docblock — long enough for a hosted round trip, short
 *  enough that stale intent never rides along with an unrelated purchase. */
export const PENDING_CHECKOUT_TTL_MS = 2 * 60 * 60 * 1000;

export interface PendingCheckout {
  /** The tender the buyer came from, when there was one. */
  tenderId?: string;
  /** The plan they picked, once they have picked one. */
  planKey?: string;
  /**
   * `'pay'` once the visitor has actually PRESSED a pay control.
   *
   * Absent means "this stash is only context" — it was written on a `/plans`
   * mount from `?tender=`, which happens to anyone who taps a paywall link,
   * including people who then just registered to read the catalogue. Without
   * this flag `postAuthPath` could not tell the two apart, so every signup that
   * had ever brushed against `/plans` came back to `/plans` with a checkout
   * sheet opening by itself (ljurayev105, 2026-09-21).
   *
   * Optional, and READ leniently: stashes written before this field existed
   * carry no `intent`, and must degrade to "no intent" rather than to a
   * surprise checkout.
   */
  intent?: 'pay';
  /** Epoch ms of the last write — the TTL is measured from here. */
  ts: number;
}

/** Opaque-token shape: a cuid or a plan key, never a path or a URL. */
const SAFE_TOKEN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * The single gate every id passes on its way in AND on its way out. Exported
 * because the callers that build a path from a raw query param need the same
 * answer without going through storage.
 */
export function isSafeCheckoutToken(value: unknown): value is string {
  return typeof value === 'string' && SAFE_TOKEN.test(value);
}

function parse(raw: string | null): PendingCheckout | null {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const row = value as Record<string, unknown>;
  const ts = typeof row.ts === 'number' ? row.ts : 0;
  if (!ts || Date.now() - ts > PENDING_CHECKOUT_TTL_MS) return null;
  const tenderId = isSafeCheckoutToken(row.tenderId) ? row.tenderId : undefined;
  const planKey = isSafeCheckoutToken(row.planKey) ? row.planKey : undefined;
  // Anything but the one literal we write is "no intent" — an older stash has
  // no such field at all, and a hand-edited one must not be able to invent one.
  const intent = row.intent === 'pay' ? ('pay' as const) : undefined;
  // A row with neither field left is not context, it is noise.
  if (!tenderId && !planKey) return null;
  return {
    ts,
    ...(tenderId ? { tenderId } : {}),
    ...(planKey ? { planKey } : {}),
    ...(intent ? { intent } : {}),
  };
}

/**
 * Record (or extend) the pending checkout.
 *
 * MERGES rather than replaces: the tender id arrives first, from the `?tender`
 * param `/plans` was opened with, and the plan key only once the buyer picks a
 * card. A plain overwrite at the second step would drop the tender — which is
 * the entire point of the stash.
 *
 * An expired row is treated as absent, so a stale tender is never resurrected
 * by an unrelated plan pick.
 */
export function stashPendingCheckout(patch: {
  tenderId?: string | null;
  planKey?: string | null;
  /** Set `'pay'` ONLY from a control the visitor pressed to pay. */
  intent?: 'pay';
}): void {
  if (typeof window === 'undefined') return;
  const tenderId = isSafeCheckoutToken(patch.tenderId) ? patch.tenderId : undefined;
  const planKey = isSafeCheckoutToken(patch.planKey) ? patch.planKey : undefined;
  if (!tenderId && !planKey) return;
  try {
    const prev = parse(window.localStorage.getItem(PENDING_CHECKOUT_KEY));
    // Intent is STICKY, like the two ids: a pressed «Подписаться» followed by a
    // re-mount of /plans (which writes only the tender) must not silently
    // downgrade the buyer back to "was only looking".
    const intent = patch.intent ?? prev?.intent;
    const next: PendingCheckout = {
      ts: Date.now(),
      ...((tenderId ?? prev?.tenderId) ? { tenderId: tenderId ?? prev?.tenderId } : {}),
      ...((planKey ?? prev?.planKey) ? { planKey: planKey ?? prev?.planKey } : {}),
      ...(intent ? { intent } : {}),
    };
    window.localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private mode / quota). The checkout still works;
    // only the return-to-tender courtesy is lost.
  }
}

/** Peek WITHOUT consuming — for decisions taken mid-flow (where to land after
 *  sign-in), which are followed by more steps that still need the context. */
export function readPendingCheckout(): PendingCheckout | null {
  if (typeof window === 'undefined') return null;
  try {
    return parse(window.localStorage.getItem(PENDING_CHECKOUT_KEY));
  } catch {
    return null;
  }
}

/** Read and clear — for the LAST use, after the money has moved. One-shot, so a
 *  second purchase in the same browser cannot inherit this tender. */
export function takePendingCheckout(): PendingCheckout | null {
  const value = readPendingCheckout();
  clearPendingCheckout();
  return value;
}

export function clearPendingCheckout(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    // no-op
  }
}

/**
 * `/plans` with the arrival context attached — `src` for `plans_viewed.source`
 * (the param that page already reads), `tender` for this stash, and `plan` for
 * the checkout `/plans` re-opens by itself (`resumedCheckout` there).
 *
 * `planKey` is what turns "back to the price list" into "back to the sheet you
 * were paying on": the retry after a refused card (`/billing/return`) has to
 * land on the SAME plan, not on a grid the buyer has to read again. Both ids go
 * through the same `isSafeCheckoutToken` gate as everything else here — they
 * end up in a URL and, for the tender, in a path.
 *
 * The path is BARE: callers hand it to `LocaleLink` / `useLocaleNavigate`,
 * which add the `/uz` prefix for an Uzbek visitor.
 */
export function plansPathWithContext(
  src: string,
  tenderId?: string | null,
  planKey?: string | null,
): string {
  const qs = new URLSearchParams({ src });
  if (isSafeCheckoutToken(tenderId)) qs.set('tender', tenderId);
  if (isSafeCheckoutToken(planKey)) qs.set('plan', planKey);
  return `/plans?${qs.toString()}`;
}
